import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import { formatJOD } from "../components/ProductCard";
import { toast } from "sonner";
import { RefreshCcw, Eye } from "lucide-react";

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function Account() {
  const { user } = useAuth();
  const { addItem } = useCart();
  const { t, tCity } = useLang();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data));
    api.get("/account/addresses").then((r) => setAddresses(r.data || []));
  }, []);

  const reorder = async (id) => {
    try {
      const { data } = await api.post(`/orders/${id}/reorder`);
      if (!data.items.length) {
        toast.error(t("common.toast.reorderEmpty"));
        return;
      }
      data.items.forEach((it) =>
        addItem(
          { id: it.product_id, slug: it.slug, name: it.name, price: it.price, sale_price: null, images: [it.image] },
          it.quantity,
          it.variant_id ? { id: it.variant_id, label: it.variant_label, price: it.price, sale_price: null, stock: 999 } : null
        )
      );
      toast.success(`${data.items.length} ${t("common.toast.reorderAdded")}`);
      navigate("/cart");
    } catch {
      toast.error(t("common.toast.reorderFailed"));
    }
  };

  if (!user || user === false) return null;

  return (
    <div data-testid="account-page" className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">{t("account.title")}</div>
      <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl tracking-tight">
        {t("account.hello")}, {user.name?.split(" ")[0]}.
      </h1>
      <div className="mt-2 text-sm font-mono text-slate-500">{user.email}</div>

      {addresses.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display font-extrabold text-2xl">{t("account.savedAddresses")}</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {addresses.map((a, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)]">
                <div className="font-display font-bold">{a.full_name}</div>
                <div className="mt-1 text-sm font-mono text-slate-600">{a.phone}</div>
                <div className="mt-1 text-sm font-mono text-slate-600">{a.address}, {tCity(a.city)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12">
        <h2 className="font-display font-extrabold text-2xl">{t("account.orders")}</h2>
        {orders.length === 0 ? (
          <div className="mt-6 bg-secondary rounded-2xl p-12 text-center text-slate-500 font-mono">
            {t("account.noOrders")}
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {orders.map((o) => (
              <div
                key={o.id}
                data-testid={`order-${o.id}`}
                className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] grid lg:grid-cols-6 gap-4 items-center"
              >
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("account.orderId")}</div>
                  <div className="mt-1 font-mono text-sm truncate">#{o.id.slice(0, 8).toUpperCase()}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("account.date")}</div>
                  <div className="mt-1 font-mono text-sm">{new Date(o.created_at).toLocaleDateString()}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("account.items")}</div>
                  <div className="mt-1 text-sm">{o.items.length}</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{t("common.total")}</div>
                  <div className="mt-1 font-display font-bold">{formatJOD(o.total)}</div>
                </div>
                <div>
                  <span
                    className={`inline-block px-2 py-1 text-[11px] font-mono uppercase tracking-wider rounded-full ${
                      STATUS_COLORS[o.status] || ""
                    }`}
                  >
                    {t(`status.${o.status}`)}
                  </span>
                </div>
                <div className="flex justify-end gap-2">
                  <Link
                    to={`/track/${o.id}`}
                    data-testid={`track-${o.id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-secondary text-xs font-mono uppercase tracking-widest hover:bg-ink hover:text-white transition-colors"
                  >
                    <Eye size={12} /> {t("account.track")}
                  </Link>
                  <button
                    onClick={() => reorder(o.id)}
                    data-testid={`reorder-${o.id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-ink text-white text-xs font-mono uppercase tracking-widest hover:bg-electric transition-colors"
                  >
                    <RefreshCcw size={12} /> {t("account.reorder")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
